import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Seo from "../Components/Seo.jsx";

import api from "../config/axiosConfig.jsx";
import leaf from "../assets/Images/leaf.png";
import defaultImg from "../assets/Images/event-default.jpg";
import { buildEventPath, findEventBySlug } from "../utils/eventSlug.js";
import { eventPage, absoluteUrl } from "../seo/schema.js";
import ShareButtons from "../Components/ShareButtons.jsx";
import Breadcrumbs from "../Components/Breadcrumbs.jsx";
import { langOf } from "../utils/lang.js";

// Show the date/time exactly as the API sends it (no timezone conversion).
// Reading with timeZone: "UTC" reads back the literal digits in the string
// (e.g. "...T14:00:00Z" -> 14:00) the same way for every visitor, instead of
// silently shifting by whatever timezone the visitor's browser happens to be in.
const formatEventDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-AU", {
    timeZone: "UTC",
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatEventTime = (timeString) => {
  if (!timeString) return "";
  const date = new Date(timeString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-AU", {
    timeZone: "UTC",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const EventDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);

        // The API has no slug lookup, so pull the active events list and
        // match by title-slug (the id is no longer part of the URL).
        const response = await api.get(
          "/website/articles/type/Event/status/active",
        );
        const { event: article, legacy } = response.data.success
          ? findEventBySlug(response.data.data?.articles, slug)
          : { event: null, legacy: false };

        // Old links used the Tamil-script slug: move to the ASCII URL.
        if (article && legacy) {
          navigate(`${buildEventPath(article)}/`, { replace: true });
          return;
        }

        if (article) {
          setEvent(article);
          const firstImage = article.assets?.find(
            (asset) => asset.asset_type === "img",
          );
          setActiveImage(firstImage?.asset_value || null);
        } else {
          setError("This event could not be found.");
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        setError("This event could not be found.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchEvent();
  }, [slug, navigate]);

  const images = event?.assets?.filter((a) => a.asset_type === "img") || [];
  const videoAsset = event?.assets?.find((a) => a.asset_type === "vid");
  const docAsset = event?.assets?.find((a) => a.asset_type === "doc");

  if (loading) {
    return (
      <div className="container-fluid pt-5 px-0">
        <section className="py-5">
          <div className="container mt-5 py-5">
            <div
              className="skeleton mb-4"
              style={{ width: "140px", height: "16px" }}
            ></div>

            <div className="row g-4 g-lg-5 align-items-start">
              {/* Left: poster + thumbnails + details card */}
              <div className="col-12 col-lg-4">
                <div
                  className="skeleton rounded-4"
                  style={{ width: "100%", height: "320px" }}
                ></div>

                <div className="row g-2 mt-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div className="col-3" key={i}>
                      <div
                        className="skeleton rounded-3"
                        style={{ height: "80px" }}
                      ></div>
                    </div>
                  ))}
                </div>

                <div className="rounded-4 border p-4 p-md-5 mt-4">
                  <div
                    className="skeleton mb-4"
                    style={{ width: "140px", height: "20px" }}
                  ></div>

                  {[1, 2, 3].map((i) => (
                    <div
                      className="d-flex align-items-start gap-3 mb-3"
                      key={i}
                    >
                      <div
                        className="skeleton rounded-circle flex-shrink-0"
                        style={{ width: "22px", height: "22px" }}
                      ></div>
                      <div className="flex-grow-1">
                        <div
                          className="skeleton mb-2"
                          style={{ width: "60px", height: "12px" }}
                        ></div>
                        <div
                          className="skeleton"
                          style={{ width: "80%", height: "14px" }}
                        ></div>
                      </div>
                    </div>
                  ))}

                  <div
                    className="skeleton rounded-pill mt-4"
                    style={{ width: "160px", height: "40px" }}
                  ></div>
                </div>
              </div>

              {/* Right: title + pills + description */}
              <div className="col-12 col-lg-8">
                <div
                  className="skeleton mb-3"
                  style={{ width: "70%", height: "36px" }}
                ></div>

                <div className="d-flex flex-wrap gap-3 mb-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      className="skeleton rounded-pill"
                      style={{ width: "150px", height: "38px" }}
                      key={i}
                    ></div>
                  ))}
                </div>

                <div
                  className="skeleton mb-3"
                  style={{ width: "160px", height: "18px" }}
                ></div>

                {[100, 95, 90, 85, 60].map((width, i) => (
                  <div
                    key={i}
                    className="skeleton mb-2"
                    style={{ width: `${width}%`, height: "16px" }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container py-5 my-5 text-center">
        <Seo
          path={`/events/${slug}/`}
          title="Event Not Found | Western Sydney Tamil Study Centre"
          description="This event could not be found."
          noindex
        />
        <Breadcrumbs
          className="d-flex justify-content-center mb-4"
          items={[
            { label: "Home", href: "/", icon: "bi-house" },
            { label: "Events", href: "/events/" },
            { label: "Event not found" },
          ]}
        />
        <h2 className="fw-bold mb-3">Event Not Found</h2>
        <p className="text-muted mb-4">
          {error || "We couldn't find the event you're looking for."}
        </p>
        <button
          type="button"
          className="btn blue-bg text-white rounded-pill px-4 py-2 border-0 fw-semibold"
          onClick={() => navigate("/")}
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <>
      <Seo {...eventPage(event)} />

      <div className="container-fluid pt-5 px-0">
        <section className="py-5">
          <div className="container mt-5 py-5">
            <Breadcrumbs
              className="mb-4"
              items={[
                { label: "Home", href: "/", icon: "bi-house" },
                { label: "Events", href: "/events/" },
                { label: event.article_title, lang: langOf(event.article_title) },
              ]}
            />

            <div className="row g-4 g-lg-5 align-items-start">
              {/* Left: Image + gallery + event details card */}
              <div className="col-12 col-lg-4">
                <div className="event-poster-wrap position-relative rounded-4 overflow-hidden shadow-sm">
                  <img
                    src={activeImage || defaultImg}
                    alt={event.article_title}
                    className="w-100 h-auto d-block"
                  />
                </div>

                {/* Gallery thumbnails */}
                {images.length > 1 && (
                  <div className="row g-2 mt-3">
                    {images.map((asset) => (
                      <div className="col-3" key={asset.asid}>
                        <button
                          type="button"
                          onClick={() => setActiveImage(asset.asset_value)}
                          className="p-0 border-0 bg-transparent w-100"
                        >
                          <img
                            src={asset.asset_value}
                            alt={event.article_title}
                            className={`w-100 rounded-3 object-fit-cover ${
                              activeImage === asset.asset_value
                                ? "border border-3 border-success"
                                : ""
                            }`}
                            style={{ height: "80px" }}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Event details card */}
                <div className="event-info-card rounded-4 shadow-sm border p-4 p-md-5 bg-white mt-4">
                  <h5
                    className="fw-bold mb-4 red-text text-uppercase"
                    style={{ letterSpacing: "0.5px" }}
                  >
                    Event Details
                  </h5>

                  <div className="d-flex flex-column gap-3">
                    {event.event_venue?.start_date && (
                      <div className="d-flex align-items-start gap-3">
                        <i className="bi bi-calendar2-date blue-text fs-5"></i>
                        <div>
                          <p className="fw-semibold mb-0">Date</p>
                          <p className="text-muted mb-0">
                            {formatEventDate(event.event_venue.start_date)}
                            {event.event_venue.end_date &&
                              event.event_venue.end_date !==
                                event.event_venue.start_date &&
                              ` - ${formatEventDate(event.event_venue.end_date)}`}
                          </p>
                        </div>
                      </div>
                    )}

                    {event.event_venue?.start_time && (
                      <div className="d-flex align-items-start gap-3">
                        <i className="bi bi-clock blue-text fs-5"></i>
                        <div>
                          <p className="fw-semibold mb-0">Time</p>
                          <p className="text-muted mb-0">
                            {formatEventTime(event.event_venue.start_time)}
                            {event.event_venue.end_time &&
                              ` - ${formatEventTime(event.event_venue.end_time)}`}
                          </p>
                        </div>
                      </div>
                    )}

                    {event.event_venue?.venue && (
                      <div className="d-flex align-items-start gap-3">
                        <i className="bi bi-geo-alt blue-text fs-5"></i>
                        <div>
                          <p className="fw-semibold mb-0">Venue</p>
                          <p className="text-muted mb-0">
                            {event.event_venue.venue}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* <button
                    type="button"
                    onClick={() => navigate("/events")}
                    className="btn blue-bg text-white rounded-pill px-4 py-2 border-0 fw-semibold mt-4"
                  >
                    Book / View Tickets
                  </button> */}
                </div>
              </div>

              {/* Right: Title + date/time/venue pills + description */}
              <div className="col-12 col-lg-8">
                <h1
                  lang={langOf(event.article_title)}
                  className="fw-bold mb-3 red-text"
                  style={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                  }}
                >
                  {event.article_title}
                </h1>

                <div className="d-flex flex-wrap gap-3 mb-4">
                  {event.event_venue?.start_date && (
                    <span className="d-inline-flex align-items-center gap-2 green-soft rounded-pill px-3 py-2 small fw-semibold">
                      <i className="bi bi-calendar2-date blue-text"></i>
                      {formatEventDate(event.event_venue.start_date)}
                    </span>
                  )}

                  {event.event_venue?.start_time && (
                    <span className="d-inline-flex align-items-center gap-2 red-soft rounded-pill px-3 py-2 small fw-semibold">
                      <i className="bi bi-clock red-text"></i>
                      {formatEventTime(event.event_venue.start_time)}
                    </span>
                  )}

                  {event.event_venue?.venue && (
                    <span className="d-inline-flex align-items-center gap-2 green-soft rounded-pill px-3 py-2 small fw-semibold">
                      <i className="bi bi-geo-alt blue-text"></i>
                      {event.event_venue.venue}
                    </span>
                  )}
                </div>

                {/* Description - below the title/date/time/venue pills */}
                <div>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <img src={leaf} alt="" style={{ height: "26px" }} />
                    <span className="text-uppercase fw-bold blue-text small">
                      About this event
                    </span>
                  </div>

                  {event.article_shortdesc && (
                    <p className="fs-5 text-muted fst-italic border-start border-4 border-danger ps-3">
                      {event.article_shortdesc}
                    </p>
                  )}

                  {event.article_longdesc ? (
                    <div
                      className="event-longdesc mt-3"
                      dangerouslySetInnerHTML={{
                        __html: event.article_longdesc,
                      }}
                    />
                  ) : (
                    <p className="text-muted mt-3">
                      More details for this event will be shared soon.
                    </p>
                  )}
                </div>

                {/* Share this event (WhatsApp, Facebook, X, LinkedIn, email, copy link) */}
                <div className="event-share border-top pt-4 mt-4">
                  <ShareButtons
                    url={new URL(absoluteUrl(eventPage(event).path)).href}
                    title={event.article_title}
                    text={[
                      event.article_title,
                      event.event_venue?.start_date && formatEventDate(event.event_venue.start_date),
                      event.event_venue?.venue,
                    ]
                      .filter(Boolean)
                      .join(" – ")}
                    itemId={event.aid ? String(event.aid) : undefined}
                  />
                </div>
              </div>
            </div>

            {/* Video */}
            {videoAsset?.asset_value && (
              <div className="mt-5">
                <h5 className="fw-bold mb-3">Event Video</h5>
                <div className="ratio ratio-16x9">
                  <iframe
                    src={videoAsset.asset_value}
                    title="Event video"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}

            {/* Document */}
            {docAsset?.asset_value && (
              <div className="mt-5">
                <a
                  href={docAsset.asset_value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark rounded-pill px-4 d-inline-flex align-items-center gap-2"
                >
                  <i className="bi bi-file-earmark-arrow-down"></i>
                  Download Event Document
                </a>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default EventDetails;
